import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Editor } from '@tiptap/core';

export type FreePositionValue = { x: number; y: number; width: number; flowHeight?: number };
export const FREE_POSITION_TYPES = ['paragraph', 'heading', 'bulletList', 'orderedList', 'taskList', 'blockquote', 'codeBlock', 'details', 'callout', 'table', 'imageBlock', 'fileBlock', 'embedBlock', 'fieldBlock', 'horizontalRule'];
export function readFreePosition(value: unknown): FreePositionValue | null {
  if (!value || typeof value !== 'object') return null;
  const p = value as FreePositionValue;
  return [p.x, p.y, p.width].every(Number.isFinite) && p.x >= 0 && p.y >= 0 && p.width >= 80
    ? { x: p.x, y: p.y, width: p.width, ...(Number.isFinite(p.flowHeight) && p.flowHeight! > 0 ? { flowHeight: p.flowHeight } : {}) } : null;
}
const modeKey = new PluginKey('gam-free-position');
export function setFreePositionMode(editor: Editor, active: boolean) {
  editor.view.dispatch(editor.state.tr.setMeta(modeKey, active).setMeta('addToHistory', false));
}
export const FreePosition = Extension.create({
  name: 'freePosition',
  addGlobalAttributes() {
    return [{ types: FREE_POSITION_TYPES, attributes: { freePosition: {
      default: null, keepOnSplit: false,
      parseHTML: el => { try { return readFreePosition(JSON.parse(el.getAttribute('data-free-position') || 'null')); } catch { return null; } },
      renderHTML: attrs => { const p = readFreePosition(attrs.freePosition); return p ? { 'data-free-position': JSON.stringify(p) } : {}; },
    } } }];
  },
  addKeyboardShortcuts() {
    return { Enter: () => {
      const { $from } = this.editor.state.selection;
      if ($from.depth === 1 && $from.parent.isTextblock && readFreePosition($from.parent.attrs.freePosition)) return this.editor.commands.setHardBreak();
      return false;
    } };
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    return [new Plugin<boolean>({
      key: modeKey,
      state: { init: () => false, apply: (tr, active) => typeof tr.getMeta(modeKey) === 'boolean' ? tr.getMeta(modeKey) : active },
      props: {
        decorations(state) {
          const decorations: Decoration[] = [];
          state.doc.forEach((node, pos) => {
            const p = readFreePosition(node.attrs.freePosition);
            if (!p) return;
            if (p.flowHeight) decorations.push(Decoration.widget(pos, () => {
              const space = document.createElement('div'); space.className = 'gam-flow-reservation';
              space.contentEditable = 'false'; space.style.height = `${p.flowHeight}px`;
              return space;
            }, { key: `flow-${pos}-${p.flowHeight}`, side: -1 }));
            decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: 'gam-positioned-block', style: `position:absolute;left:${p.x}px;top:${p.y}px;width:${p.width}px;margin:0;` }));
            if (!modeKey.getState(state) || !editor.isEditable) return;
            decorations.push(Decoration.widget(pos, view => {
              const bar = document.createElement('span');
              bar.className = 'gam-position-controls'; bar.contentEditable = 'false';
              bar.style.cssText = `left:${p.x}px;top:${Math.max(0, p.y - 26)}px`;
              for (const action of ['move', 'resize', 'flow'] as const) {
                const button = document.createElement('button'); button.type = 'button';
                button.textContent = { move: '✥', resize: '↔', flow: '↩' }[action];
                button.title = { move: 'הזז בלוק', resize: 'שנה רוחב', flow: 'החזר לזרימת המסמך' }[action];
                button.setAttribute('aria-label', button.title);
                button.onpointerdown = event => {
                  if (!editor.isEditable || event.button !== 0) return;
                  event.preventDefault(); event.stopPropagation();
                  if (action === 'flow') { view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, freePosition: null })); return; }
                  const initialDoc = view.state.doc;
                  const element = view.nodeDOM(pos) as HTMLElement;
                  const scale = view.dom.getBoundingClientRect().width / view.dom.offsetWidth || 1;
                  const start = { x: event.clientX, y: event.clientY };
                  let next = p;
                  const move = (e: PointerEvent) => {
                    const dx = (e.clientX - start.x) / scale, dy = (e.clientY - start.y) / scale;
                    next = action === 'move' ? { ...p, x: Math.max(0, Math.min(p.x + dx, view.dom.clientWidth - p.width)), y: Math.max(26, p.y + dy) }
                      : { ...p, width: Math.max(80, Math.min(p.width + dx, view.dom.clientWidth - p.x)) };
                    element.style.left = `${next.x}px`; element.style.top = `${next.y}px`; element.style.width = `${next.width}px`;
                    bar.style.left = `${next.x}px`; bar.style.top = `${Math.max(0, next.y - 26)}px`;
                  };
                  const finish = (e: PointerEvent) => {
                    window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', finish); window.removeEventListener('pointercancel', finish);
                    if (editor.isDestroyed) return;
                    if (e.type !== 'pointercancel' && editor.isEditable && view.state.doc === initialDoc) view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, freePosition: next }));
                    else {
                      const current = readFreePosition(view.state.doc.nodeAt(pos)?.attrs.freePosition) || p;
                      element.style.left = `${current.x}px`; element.style.top = `${current.y}px`; element.style.width = `${current.width}px`;
                      view.dispatch(view.state.tr.setMeta('addToHistory', false));
                    }
                  };
                  window.addEventListener('pointermove', move); window.addEventListener('pointerup', finish); window.addEventListener('pointercancel', finish);
                };
                bar.appendChild(button);
              }
              return bar;
            }, { key: `free-${pos}-${JSON.stringify(p)}`, stopEvent: () => true }));
          });
          return DecorationSet.create(state.doc, decorations);
        },
        handleDOMEvents: { mousedown(view, event) {
          const emptySpace = event.target === view.dom || (event.target instanceof HTMLElement && event.target.classList.contains('gam-flow-reservation'));
          if (!modeKey.getState(view.state) || !editor.isEditable || !emptySpace || event.button !== 0) return false;
          event.preventDefault();
          const rect = view.dom.getBoundingClientRect(), scale = rect.width / view.dom.offsetWidth || 1;
          const width = Math.min(320, Math.max(80, view.dom.clientWidth - 40));
          const p = { x: Math.max(0, Math.min((event.clientX - rect.left) / scale, view.dom.clientWidth - width)), y: Math.max(26, (event.clientY - rect.top) / scale), width };
          const pos = view.state.doc.content.size;
          const node = view.state.schema.nodes.paragraph.create({ freePosition: p });
          const tr = view.state.tr.insert(pos, node);
          view.dispatch(tr.setSelection(TextSelection.create(tr.doc, pos + 1))); view.focus();
          return true;
        } },
      },
      view(view) {
        let frame = 0;
        const measure = () => {
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(() => {
            view.dom.classList.toggle('gam-free-position-active', !!modeKey.getState(view.state));
            let bottom = 0;
            const root = view.dom.getBoundingClientRect(), scale = root.width / view.dom.offsetWidth || 1;
            view.state.doc.forEach((node, pos) => {
              const el = view.nodeDOM(pos) as HTMLElement | null;
              if (!el?.getBoundingClientRect) return;
              const r = el.getBoundingClientRect(); bottom = Math.max(bottom, (r.bottom - root.top) / scale);
            });
            view.dom.style.minHeight = `${Math.max(modeKey.getState(view.state) ? 800 : 200, bottom + 120)}px`;
          });
        };
        const observer = new ResizeObserver(measure);
        for (const child of Array.from(view.dom.children)) observer.observe(child);
        measure();
        return { update() { observer.disconnect(); for (const child of Array.from(view.dom.children)) observer.observe(child); measure(); }, destroy() { cancelAnimationFrame(frame); observer.disconnect(); } };
      },
    })];
  },
});
